import { action, autorun, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { getCustomerPrice } from 'api/user';

class SpecialistOrderFormState {

    specialist = null;
    amount = 1;
    selectedEvents = new Array(1).map(x => null);
    price = null;
    submitting = false;
    submitSuccess = false;
    pairVisit = false;
    offlineVisit = false;
    ticketType = null;
    username = null;
    problem = null;
    order = null;

    constructor(model) {
        makeObservable(this, {
            model: false,
            setSpecialist: action.bound,
            specialist: observable,
            amount: observable,
            setAmount: action.bound,
            selectedEvents: observable,
            setSelectedEvents: action.bound,
            resetSelectedEvents: action.bound,
            workingOffline: computed,
            workingWithPairs: computed,
            price: observable,
            setPrice: action.bound,
            submitting: observable,
            setSubmitting: action.bound,
            submitSuccess: observable,
            setSubmitSuccess: action.bound,
            pairVisit: observable,
            setPairVisit: action.bound,
            offlineVisit: observable,
            setOfflineVisit: action.bound,
            hasSelectedEvents: computed,
            username: observable,
            setUsername: action.bound,
            problem: observable,
            setProblem: action.bound,
            order: observable,
            setOrder: action.bound,
            ticketType: observable,
            setTicketType: action.bound
        });
        this.model = model;
    }

    init = () => {

        this.disposer = [];

        autorun(() => {
            const price = this.specialist ? this.specialist.data.servicesSet[0].price : null;
            this.setPrice(price);
        });

        autorun(() => {
            const { workingOffline } = this.model.specialistFilter.filters;
            runInAction(() => {
                this.offlineVisit = workingOffline;
            })
            if (this.specialist) {
                const onlineCount = this.specialist.onlineCount;
                const offlineCount = this.specialist.offlineCount;
                if (onlineCount !== null && offlineCount !== null) {
                    if (offlineCount > 0 && onlineCount <= 0) {
                        runInAction(() => {
                            this.offlineVisit = true;
                        });
                    }
                }
            }
        });

        autorun(() => {
            const { workingWithPairs, problems: problem } = this.model.specialistFilter.filters;
            runInAction(() => {
                this.pairVisit = workingWithPairs;
                this.problem = problem;
            })
        })

        this.disposer.push(
            reaction(() => this.amount,
                (amount) => {
                    this.resetSelectedEvents(amount);
                }),
            reaction(() => ({ amount:this.amount, specialist: this.specialist}),
                () => {
                    let price;
                    if (this.specialist) {
                        if (this.amount > 1) {
                            price = this.specialist.ticketOptions.find(to => to.visit === this.amount).price;
                        } else {
                            price = this.specialist.data.servicesSet[0].price
                        }
                    } else {
                        price = null;
                    }
                    this.setPrice(price);
                }),
            reaction(() => ({ amount:this.amount, specialist:this.specialist, offlineVisit: this.offlineVisit, username: this.username}),
                () => {
                    if (this.specialist) {
                        const meetingType = this.offlineVisit ? 'OFF' : 'ON';
                        const service = this.specialist.data.servicesSet.find(ss => ss.meetingType === meetingType);
                        const visits = this.amount;
                        if (this.model.user.isAuthenticated) {
                            if ( service?.id && visits) {
                                getCustomerPrice({service: service.id, visits, phone: this.model.user.username}).then((customerPrice) => {
                                    this.setPrice(customerPrice);
                                })
                            }
                        } else {
                            const username = this.username;
                            if ( service?.id && visits && username) {
                                getCustomerPrice({service: service.id, visits, phone: username}).then((customerPrice) => {
                                    this.setPrice(customerPrice);
                                })
                            }
                        }
                    } else {
                        this.setPrice(null);
                    }
                })
        );
    };

    setOrder(order) {
        this.order = order;
    }

    setSubmitting(value) {
        this.submitting = value;
    }

    setUsername(value) {
        this.username = value;
    }

    setSubmitSuccess(value) {
        this.submitSuccess = value;
    }

    setTicketType(value) {
        this.ticketType = value;
    };

    reset = () => {
        console.log('reset');
        runInAction(() => {
            this.amount = 1;
            this.selectedEvents = new Array(1).map(x => null);
            this.price = 0;
            this.submitting = false;
            this.submitSuccess = false;
            this.offlineVisit = false;
            this.pairVisit = false;
            this.problem = null;
            this.ticketType = null;
            this.order = null;

        });
        this.disposer.forEach(dispose => dispose());
    };

    setPairVisit(value) {
        this.pairVisit = value;
    }

    setOfflineVisit(value) {
        this.offlineVisit = value;
    }

    setPrice(price) {
        this.price = price;
    }

    get workingOffline() {
        return this.specialist.workingOffline;
    }

    get workingWithPairs() {
        return this.model.filter.filters.workingWithPairs;
    }

    resetSelectedEvents(amount, reset = false) {
        if (reset) {
            this.selectedEvents = new Array(amount).map(x => null);
            return;
        }
        let newSelectedEvents = this.selectedEvents.slice(0, amount);
        if (amount >= this.selectedEvents.length) {
            newSelectedEvents = [
                ...newSelectedEvents,
                ...[ ...new Array(amount - this.selectedEvents.length) ].map(x => null)
            ];
        }
        this.selectedEvents = newSelectedEvents;
    }

    setSelectedEvents(index, value) {
        const oldEvents = this.selectedEvents;
        const newEvents = [
            ...oldEvents.slice(0, index),
            value,
            ...oldEvents.slice(index + 1)
        ];
        this.selectedEvents = newEvents;
    }

    setAmount(data) {
        this.amount = data;
    }

    setSpecialist(id) {
        if (id) {
            this.specialist = this.model.specialist.specialists.find(spec => spec.id === id);
        } else {
            this.specialist = null;
        }
    }

    get ticketOptions() {
        const availableSlots = this.specialistEvents.length;
        return this.specialist.ticketOptions.filter(({ visit }) => visit <= availableSlots);
    }

    get hasSelectedEvents() {
        return this.selectedEvents.some(event => typeof ( event ) !== 'undefined' && event !== null);
    }

    setProblem(problemId) {
        this.problem = problemId;
    }

    get specialistEvents() {
        if (!this.specialist) { return [] }
        const meetingType = this.offlineVisit ? 'OFF' : 'ON';
        return this.specialist.filterEvents({ meetingType }).map(e => e.startDate);
    }

}

export default SpecialistOrderFormState;
